import { mercuryApi } from '$api'
import { KeyValueBindingModel } from '$components/common'
import {
  CompanyPreferences,
  logger,
  enqueueLoadedSnackbar,
  exceptionMessageHandler,
} from '$utils'
import { getCountryCodeFromLocale } from 'src/components/common/helpers/locale/companyLocale'
import { SharedSlice } from 'src/components/slice'
import { StateCreator } from 'zustand'
import {
  ActiveCompany,
  ICompanyDefaultWorkingHours,
  IPayrollPayType,
  SalesTax,
} from '$models'
import { PosItem } from '../_common/interfaces/posItem'
import { PayrollSystem } from '$constants'
import {
  payrollPayTypeService,
  salesTaxService,
  settingsService,
} from '$services'
import { IPayrollPayTypeExtended } from './_common/helpers/payrollPayTypeExtended'
import dayjs from 'dayjs'

interface CompanyPreferencesState {
  defaultPayrollPayTypes: IPayrollPayTypeExtended[]
  customPayrollPayTypes: IPayrollPayTypeExtended[]
  localeSalesTax: SalesTax[]
  companySalesTax: SalesTax[]
  posItems: PosItem[]
  defaultWorkingHours: ICompanyDefaultWorkingHours[]
}

export interface CompanyPreferencesSlice extends CompanyPreferencesState {
  /**
   * Get all company default working hours
   */
  getCompanyDefaultWorkingHours: () => Promise<void>

  /**
   * Updated all the company default working hours
   * @param companyDefaultWorkingHours
   */
  updateCompanyDefaultWorkingHours: (
    companyDefaultWorkingHours: ICompanyDefaultWorkingHours[]
  ) => Promise<void>

  /**
   * Set the company default working hours
   * @param companyDefaultWorkingHoursId - the company default working hours id
   * @param propertyName - the property name to be updated
   * @param value - the value to be updated
   */
  setCompanyDefaultWorkingHours: (
    companyDefaultWorkingHoursId: string,
    propertyName: keyof ICompanyDefaultWorkingHours,
    value: string | boolean | number | null
  ) => Promise<void>

  /**
   * Get all default and company payroll pay types
   * Populates both defaultPayrollPayTypes and customDefaultPayTypes states
   */
  getPayrollPayTypes: () => Promise<void>

  /**
   * Get the company's local sales tax (e.g. NZ or AU specific tax rates)
   * @param activeCompany the currently active company
   */
  getLocaleSalesTax: (activeCompany: ActiveCompany | null) => Promise<void>

  /**
   * Get the company's tax rates (e.g. the custom ones the company has defined)
   * @param activeCompany the currently active company
   */
  getCompanySalesTax: () => Promise<void>

  /**
   * Gets all POS items for the company
   */
  getPosItems: () => Promise<void>

  /**
   * Sets the custom payroll pay types state
   * @param updatedPayTypes the updated array of custom payroll pay types
   */
  setCustomPayrollPayTypes: (updatedPayTypes: IPayrollPayType[]) => void

  /**
   * Updates the company's sales tax with new values
   * @param rates the tax rates that are being updated
   * @param errorMessage the message to display if the request fails
   * @param successMessage the message to display if the request is sucessful
   */
  updateCompanySalesTax: (
    rates: SalesTax[],
    errorMessage?: string,
    successMessage?: string
  ) => Promise<void>

  /**
   * Updated the company's preferences
   * @param preferences the preferences that are being updated
   * @param errorMessage the message to display if the request fails
   * @param successMessage the message to display if the request is sucessful
   */
  updateCompanyPreferences: (
    preferences: KeyValueBindingModel[],
    successMessage?: string,
    errorMessage?: string
  ) => Promise<void>

  /**
   * Add a new Custom Payroll Pay Type
   * @param payTypeName the pay type name to be added
   * @param payCode the pay code to be added
   * @param errorMessage the message to display if the request fails
   * @param successMessage the message to display if the request is sucessful
   */
  addCustomPayrollPayType: (
    payTypeName: string,
    payCode: string | undefined,
    errorMessage?: string,
    successMessage?: string
  ) => Promise<void>

  /**
   * Updates a Custom Payroll Pay Type
   * @param payType the updated pay type
   * @param errorMessage the message to display if the request fails
   * @param successMessage the message to display if the request is sucessful
   */
  updateCustomPayrollPayType: (
    payType: IPayrollPayType,
    errorMessage?: string,
    successMessage?: string
  ) => Promise<void>

  /**
   * Delete a Custom Payroll Pay Type
   * @param payTypeId the pay type reference to be removed
   * @param errorMessage the message to display if the request fails
   * @param successMessage the message to display if the request is sucessful
   */
  removeCustomPayrollPayType: (
    payTypeId: string,
    errorMessage?: string,
    successMessage?: string
  ) => Promise<void>

  /**
   * Resets the CompanyPreference Slice
   */
  resetCompanyPreferencesSlice: () => void
}

const initialState: CompanyPreferencesState = {
  defaultPayrollPayTypes: [],
  customPayrollPayTypes: [],
  companySalesTax: [],
  localeSalesTax: [],
  posItems: [],
  defaultWorkingHours: [],
}

const createCompanyPreferencesSlice: StateCreator<
  CompanyPreferencesSlice & SharedSlice,
  [],
  [],
  CompanyPreferencesSlice
> = (set) => ({
  // State variables
  ...initialState,

  // State Setters
  resetCompanyPreferencesSlice: () => set(initialState),

  setCustomPayrollPayTypes: (updatedPayTypes: IPayrollPayType[]) =>
    set({ customPayrollPayTypes: updatedPayTypes }),

  getCompanyDefaultWorkingHours: async (): Promise<void> => {
    return settingsService
      .getCompanyDefaultWorkingHours()
      .then((result) =>
        set(() => ({
          defaultWorkingHours: result,
        }))
      )
      .catch((error) => {
        set(() => ({
          defaultWorkingHours: [],
        }))
        exceptionMessageHandler(
          error,
          'Failed to get the company default working hours'
        )
      })
  },

  updateCompanyDefaultWorkingHours: async (
    companyDefaultWorkingHours: ICompanyDefaultWorkingHours[]
  ) => {
    try {
      const updatedCompanyDefaultWorkingHours: ICompanyDefaultWorkingHours[] =
        await settingsService.updateCompanyDefaultWorkingHours(
          companyDefaultWorkingHours
        )
      if (
        !updatedCompanyDefaultWorkingHours ||
        updatedCompanyDefaultWorkingHours.length === 0
      ) {
        const errorMessage = `updatedCompanyDefaultWorkingHours doesn't exist`
        logger.error(errorMessage)
        throw new Error(errorMessage)
      }
      set(() => ({
        defaultWorkingHours: updatedCompanyDefaultWorkingHours,
      }))
    } catch (error) {
      exceptionMessageHandler(
        error,
        'Failed to update company default working hours'
      )
    }
  },

  setCompanyDefaultWorkingHours: async (
    companyDefaultWorkingHoursId: string,
    propertyName: keyof ICompanyDefaultWorkingHours,
    value: string | boolean | number | null
  ): Promise<void> => {
    set((state) => ({
      defaultWorkingHours: state.defaultWorkingHours.map(
        (companyDefaultWorkingHour) => {
          if (companyDefaultWorkingHour.id !== companyDefaultWorkingHoursId) {
            return companyDefaultWorkingHour
          }

          // Create updated working hour with new value
          const updatedWorkingHour = {
            ...companyDefaultWorkingHour,
            [propertyName]: value,
          } as ICompanyDefaultWorkingHours

          if (propertyName === 'isDayOff') {
            updatedWorkingHour.from = undefined
            updatedWorkingHour.to = undefined
            updatedWorkingHour.breakMinutes = undefined
          }

          // Recalculate hours when time values or break minutes change
          const isTimeValueChanged =
            propertyName === 'from' ||
            propertyName === 'to' ||
            propertyName === 'breakMinutes'

          updatedWorkingHour.isDayOff =
            isTimeValueChanged && value != null
              ? false
              : updatedWorkingHour.isDayOff

          const shouldRecalculateHours =
            isTimeValueChanged || propertyName === 'isDayOff'

          if (shouldRecalculateHours) {
            updateWorkingHoursDuration(updatedWorkingHour)
          }

          return updatedWorkingHour
        }
      ),
    }))
  },

  getPayrollPayTypes: async (): Promise<void> => {
    return mercuryApi
      .get<IPayrollPayType[]>('payroll/pay-type')
      .then((result) =>
        set(() => ({
          defaultPayrollPayTypes: result.filter(
            (payType) => payType.payrollSystem !== PayrollSystem.Custom
          ),
          customPayrollPayTypes: result.filter(
            (payType) => payType.payrollSystem === PayrollSystem.Custom
          ),
        }))
      )
      .catch((error) => {
        set(() => ({
          defaultPayrollPayTypes: [],
          customPayrollPayTypes: [],
        }))
        exceptionMessageHandler(error, 'Failed to retrieve payroll pay types')
      })
  },

  getCompanySalesTax: async (): Promise<void> => {
    return salesTaxService
      .getAllSalesTaxRates()
      .then((result) => set(() => ({ companySalesTax: result })))
      .catch((error) => {
        set(() => ({
          companySalesTax: [],
        }))
        exceptionMessageHandler(
          error,
          "Failed to retrieve your company's sales tax"
        )
      })
  },

  getLocaleSalesTax: async (
    activeCompany: ActiveCompany | null
  ): Promise<void> => {
    const companyLocale = getCountryCodeFromLocale(
      activeCompany?.preference(CompanyPreferences.Locale) as string
    )
    const companyProvince = activeCompany?.preference(
      CompanyPreferences.Province
    )

    return salesTaxService
      .getLocaleSalesTaxRates(companyLocale, companyProvince)
      .then((result) => {
        set(() => ({ localeSalesTax: result }))
      })
      .catch((error) => {
        set(() => ({ localeSalesTax: [] }))
        exceptionMessageHandler(
          error,
          "Failed to retreive your company's locale sale tax rates"
        )
      })
  },

  getPosItems: async (): Promise<void> => {
    return mercuryApi
      .get<PosItem[]>('/pos/items')
      .then((result) => set(() => ({ posItems: result })))
      .catch((error) => {
        set(() => ({
          posItems: [],
        }))
        exceptionMessageHandler(
          error,
          "Failed to retrieve your company's POS items"
        )
      })
  },

  updateCompanySalesTax: async (
    rates: SalesTax[],
    errorMessage?: string,
    successMessage?: string
  ): Promise<void> => {
    try {
      set(() => ({ isLoading: true }))

      const updatedCompanySalesTax: SalesTax[] =
        await salesTaxService.updateSaleTaxRates(rates)

      if (!updatedCompanySalesTax) {
        logger.error(`${errorMessage} - updatedCompanySalesTax doesn't exist`)
        enqueueLoadedSnackbar(errorMessage, 'error')
        return
      }

      set(() => ({
        companySalesTax: updatedCompanySalesTax,
      }))

      enqueueLoadedSnackbar(successMessage, 'success')
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  updateCompanyPreferences: async (
    preferences: KeyValueBindingModel[],
    successMessage?: string,
    errorMessage?: string
  ) => {
    try {
      set(() => ({ isLoading: true }))

      const updatedPreferences: SalesTax[] = await mercuryApi.add(
        `/preferences`,
        preferences
      )

      if (!updatedPreferences) {
        logger.error(
          `${errorMessage} - updatedCompanyPreferences doesn't exist`
        )

        enqueueLoadedSnackbar(errorMessage, 'error')
        return
      }

      enqueueLoadedSnackbar(successMessage, 'success')
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  addCustomPayrollPayType: async (
    payTypeName: string,
    payCode: string | undefined,
    errorMessage?: string,
    successMessage?: string
  ) => {
    try {
      set(() => ({ isLoading: true }))

      // This request returns only the id
      const createdPayType = await payrollPayTypeService.createPayType(
        payTypeName,
        payCode
      )

      if (!createdPayType) {
        logger.error(
          `${errorMessage} - creating a new Pay Type didn't return an id (payTypeRef)`
        )

        enqueueLoadedSnackbar(errorMessage, 'error')
        return
      }

      // I know this is not the best way of 'adding' a new object to a state but as the reponse returns just the id (PayTypeRef)
      // not the whole object so we have to map the payrollPayTypes and edit the relevant entry in the state. (Also we hold newly added pay types
      // in the state e.g. ones that have not been added to the DB yet, so this is an easy band-aid fix)
      set((state) => ({
        customPayrollPayTypes: state.customPayrollPayTypes.map(
          (currentPayType) => {
            if (currentPayType.id == createdPayType.id) {
              // For some reason the paytype POST reponse is a object with one attribute "data" that holds the id (payTypeRef)
              return createdPayType
            }
            return currentPayType
          }
        ),
      }))

      enqueueLoadedSnackbar(successMessage, 'success')
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  updateCustomPayrollPayType: async (
    payType: IPayrollPayType,
    errorMessage?: string,
    successMessage?: string
  ) => {
    try {
      set(() => ({ isLoading: true }))

      const updatedPayType = await payrollPayTypeService.updatePayType(payType)

      // I know this is not the best way of 'updating' an object in a state but as the reponse does not return anything
      // so we have to map the payrollPayTypes and edit the relevant entry in the state. (Also we hold newly added pay types
      // in the state e.g. ones that have not been added to the DB yet, so this is an easy band-aid fix)
      set((state) => ({
        customPayrollPayTypes: state.customPayrollPayTypes.map(
          (currentPayType) => {
            if (currentPayType.id == updatedPayType.id) {
              return updatedPayType
            }
            return currentPayType
          }
        ),
      }))

      enqueueLoadedSnackbar(successMessage, 'success')
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    }
  },

  removeCustomPayrollPayType: async (
    payTypeId: string,
    errorMessage?: string,
    successMessage?: string
  ) => {
    try {
      // This request returns a 204 No Content
      await payrollPayTypeService.deletePayType(payTypeId)

      set((state) => ({
        customPayrollPayTypes: state.customPayrollPayTypes.filter(
          (payType) => payType.id !== payTypeId
        ),
      }))

      enqueueLoadedSnackbar(successMessage, 'success')
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    }
  },
})

export default createCompanyPreferencesSlice

// Helper function to calculate working hours duration
const updateWorkingHoursDuration = (
  workingHour: ICompanyDefaultWorkingHours
): void => {
  const from = dayjs(workingHour.from, 'HH:mm:ss')
  const to = dayjs(workingHour.to, 'HH:mm:ss')
  const breakMinutes = workingHour.breakMinutes ?? 0

  if (from.isValid() && to.isValid()) {
    // This is the dif between from and to in minutes, minus the break minutes
    // and then divide by 60 to get the number of hours
    // Then round to 2 decimals
    workingHour.hours = Number(
      (Math.abs(to.diff(from, 'minutes') - breakMinutes) / 60).toFixed(2)
    )
  } else {
    workingHour.hours = undefined // Reset if values are invalid
  }
}
